import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './AIChat.css';
import { auth } from '../firebase';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAIContext } from '../hooks/useAIContext';
import { useMessageCache } from '../hooks/useMessageCache';
import { useAnalytics } from '../hooks/useAnalytics';
import { handleAIError, AIError, ERROR_CODES } from '../utils/errorHandler';
import { debounce } from 'lodash';

const AIChat = ({
  analysis,
  aiClient,
  selectedModel,
  specificModel,
  onAnalysisCountChange,
  analysisCount,
  setAnalysisCount,
  hexagramData,
  gender,
  ageRange,
  maritalStatus,
  job,
  concern,
  dominantEnergy,
  combinedPairs,
  notificationList,
  ghiChuList,
  warningNotice,
  maxAnalysisCount,
  userRole
}) => {
  // State
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [showVIPPricing, setShowVIPPricing] = useState(false);
  const db = getFirestore();

  // Refs
  const messagesEndRef = React.useRef(null);
  const chatMessagesRef = React.useRef(null);
  const latestUserMessageRef = React.useRef(null);

  // Custom hooks
  const { contextString, updateContext } = useAIContext({
    hexagramData,
    gender,
    ageRange,
    maritalStatus,
    job,
    concern,
    dominantEnergy,
    combinedPairs,
    notificationList,
    ghiChuList,
    warningNotice
  });

  const { getCachedResponse, setCachedResponse } = useMessageCache();
  const { trackChatInteraction, trackModelUsage, trackErrorOccurrence } = useAnalytics();

  // Memoized values
  const hasReachedLimit = useMemo(() => 
    analysisCount >= maxAnalysisCount && userRole === 'regular',
    [analysisCount, maxAnalysisCount, userRole]
  );

  const maxCharacterCount = useMemo(() => {
    switch (userRole) {
      case 'admin': return 13195;
      case 'vip': return 3000;
      default: return 500;
    }
  }, [userRole]);

  // Effects
  useEffect(() => {
    if (analysis) {
      setMessages([{ role: 'assistant', content: analysis }]);
    }
  }, [analysis]);

  useEffect(() => {
    const cleanup = () => {
      if (messages.length > 200) {
        setMessages(messages.slice(-100));
      }
    };
    cleanup();
  }, [messages.length]);

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'user') {
        setShouldAutoScroll(true);
        scrollToBottom();
      } else if (lastMessage.role === 'assistant' && !isLoading) {
        // Scroll to the latest user message after AI responds
        if (latestUserMessageRef.current) {
          latestUserMessageRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].role === 'assistant') {
      setShouldAutoScroll(true);
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (!isLoading && shouldAutoScroll) {
      scrollToBottom();
    }
  }, [isLoading, shouldAutoScroll]);

  useEffect(() => {
    if (isFocusMode) {
      document.body.classList.add('focus-mode');
    } else {
      document.body.classList.remove('focus-mode');
    }
  }, [isFocusMode]);

  // Handlers
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && shouldAutoScroll) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
      if (chatMessagesRef.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
    }
  }, [shouldAutoScroll]);

  const handleScroll = useCallback((e) => {
    if (!chatMessagesRef.current || e.target !== chatMessagesRef.current) return;
    
    const element = e.target;
    const isScrolledToBottom = 
      Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 50;
    setShouldAutoScroll(isScrolledToBottom);
  }, []);

  const handleSendMessage = async () => {
    try {
      if (!userInput.trim() || !aiClient) return;

      if (hasReachedLimit) {
        throw new AIError(
          ERROR_CODES.RATE_LIMIT_EXCEEDED,
          'Bạn đã đạt giới hạn số lần phân tích. Vui lòng nâng cấp tài khoản để tiếp tục.'
        );
      }

      setIsLoading(true);
      setShouldAutoScroll(true);

      const newMessages = [...messages, { role: 'user', content: userInput }];
      setMessages(newMessages);
      setUserInput('');

      // Check cache first
      const cachedResponse = getCachedResponse(userInput, contextString);
      if (cachedResponse) {
        setMessages([...newMessages, { role: 'assistant', content: cachedResponse }]);
        await updateAnalysisCount();
        return;
      }

      let response;
      
      // Handle different AI models
      if (selectedModel === 'gemini') {
        response = await handleGeminiChat(newMessages);
      } else if (selectedModel === 'claude') {
        response = await handleClaudeChat(newMessages);
      } else {
        response = await handleOpenAIChat(newMessages);
      }

      if (response) {
        setMessages([...newMessages, { role: 'assistant', content: response }]);
        setCachedResponse(userInput, contextString, response);
        
        // Update analysis count in both local state and Firestore
        await updateAnalysisCount();
        
        // Track analytics
        await trackChatInteraction('message_sent', {
          model: selectedModel,
          messageLength: userInput.length
        });
        await trackModelUsage(selectedModel);
      }

    } catch (error) {
      console.error('Chat Error:', error);
      const errorMessage = handleAIError(error);
      setMessages([...messages, { 
        role: 'assistant',
        content: errorMessage
      }]);
      
      await trackErrorOccurrence(error.code || 'UNKNOWN_ERROR', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateAnalysisCount = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const newCount = (userDoc.data().analysisCount || 0) + 1;
        
        // Update Firestore
        await updateDoc(userDocRef, {
          analysisCount: newCount,
          lastAnalysisTime: new Date().toISOString()
        });

        // Update local state
        setAnalysisCount(newCount);
        if (onAnalysisCountChange) {
          onAnalysisCountChange(newCount);
        }
      }
    } catch (error) {
      console.error('Error updating analysis count:', error);
    }
  };

  const handleGeminiChat = async (chatHistory) => {
    try {
      const chat = aiClient.startChat({
        history: [
          { role: 'user', parts: [{ text: contextString }] },
          { role: 'model', parts: [{ text: 'I understand the context. How can I help you analyze this data?' }] },
          ...chatHistory.map(msg => ({
            role: msg.role === 'user' ? 'user' : 'model',
            parts: [{ text: msg.content }]
          }))
        ]
      });

      const result = await chat.sendMessage([{ text: userInput }]);
      return result?.response?.text();
    } catch (error) {
      throw new AIError(ERROR_CODES.API_ERROR, 'Lỗi khi gọi Gemini API');
    }
  };

  const handleClaudeChat = async (chatHistory) => {
    try {
      const result = await aiClient.messages.create({
        model: specificModel,
        max_tokens: 8192,
        messages: [
          { role: 'system', content: contextString },
          ...chatHistory.map(msg => ({
            role: msg.role,
            content: msg.content
          }))
        ],
        temperature: 0.7
      });
      return result.content[0].text;
    } catch (error) {
      throw new AIError(ERROR_CODES.API_ERROR, 'Lỗi khi gọi Claude API');
    }
  };

  const handleOpenAIChat = async (chatHistory) => {
    try {
      const completion = await aiClient.chat.completions.create({
        model: specificModel,
        messages: [
          { role: 'system', content: contextString },
          ...chatHistory.map(msg => ({
            role: msg.role,
            content: msg.content
          }))
        ],
        temperature: 0.7,
        max_tokens: 8192
      });
      return completion.choices[0].message.content;
    } catch (error) {
      throw new AIError(ERROR_CODES.API_ERROR, 'Lỗi khi gọi OpenAI API');
    }
  };

  const toggleFocusMode = () => {
    const newFocusMode = !isFocusMode;
    setIsFocusMode(newFocusMode);
    
    document.body.style.pointerEvents = 'none';
    
    if (newFocusMode) {
      document.body.style.overflow = 'hidden';
      document.body.offsetHeight;
    } else {
      document.body.style.overflow = '';
      if (window.innerWidth <= 767) {
        const chatContainer = document.querySelector('.chat-container');
        if (chatContainer) {
          const yOffset = -20;
          const y = chatContainer.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'instant' });
        }
      }
    }
    
    setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 300);
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('chat-overlay') && !document.body.style.pointerEvents) {
      toggleFocusMode();
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter để xuống dòng - giữ nguyên hành vi mặc định
        return;
      }
      // Enter để gửi tin nhắn
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.style.pointerEvents = '';
    };
  }, []);

  return (
    <>
      <div className={`chat-overlay ${isFocusMode ? 'active' : ''}`} onClick={handleOverlayClick} />
      <div className={`chat-container ${isFocusMode ? 'active' : ''}`}>
        <div 
          className="chat-messages" 
          ref={chatMessagesRef}
          onScroll={handleScroll}
        >
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`message ${message.role}`}
              ref={message.role === 'user' && index === messages.length - 2 ? latestUserMessageRef : null}
            >
              <div>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message assistant">
              <div className="typing-indicator">
                <div className="dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className="chat-input-container">
          <div className="chat-controls">
            <div className="input-stats">
              <span className="analysis-counter">
                {analysisCount}/{maxAnalysisCount} lượt
              </span>
              <span className="char-counter">
                {userInput.length}/{maxCharacterCount} ký tự
              </span>
            </div>
            <div className="button-group">
              <button
                className={`focus-button ${isFocusMode ? 'active' : ''}`}
                onClick={toggleFocusMode}
                data-tooltip="Chế độ tập trung"
              >
                <i className="fas fa-expand"></i>
              </button>
              <button
                className="send-button"
                onClick={handleSendMessage}
                disabled={isLoading || !userInput.trim() || hasReachedLimit}
                data-tooltip="Gửi tin nhắn"
              >
                <i className="fas fa-paper-plane"></i>
              </button>
            </div>
          </div>
          <div className="chat-input-wrapper">
            <textarea
              className="chat-input"
              value={userInput}
              onChange={(e) => {
                const newValue = e.target.value;
                if (userRole === 'admin') {
                  setUserInput(newValue);
                } else {
                  setUserInput(newValue.slice(0, maxCharacterCount));
                }
              }}
              placeholder="Chat để hỏi sâu hơn..."
              onKeyDown={handleKeyDown}
              disabled={isLoading || hasReachedLimit}
            />
            <p className="chat-note">
              Lưu ý: Nội dung phân tích chỉ để tham khảo. Bạn nên liên hệ chuyên gia để được luận giải chi tiết hơn.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(AIChat);
